import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// =============================================================================
// AuthErrorBoundary
//
// Supabase Auth's verify endpoint redirects to the Site URL with error
// information in the URL fragment when an OTP / recovery / signup token
// is invalid or expired:
//
//   https://geoborder.us/?lat=…&lon=…#error=access_denied
//     &error_code=otp_expired
//     &error_description=Email+link+is+invalid+or+has+expired
//
// Without a handler the user just lands on the homepage with weird-
// looking URL chrome and no explanation. This component lives at the
// top of the router tree, watches for those hash params on every
// navigation, and reroutes the user somewhere actionable with a banner.
//
// Routing: all auth errors land on /sign-in with the description as a
// banner. SignInPage exposes both "Resend verification email" (for
// signup-token expiry) and "Forgot Password?" (for recovery-token
// expiry) as follow-up actions, so it's the right single destination
// regardless of which flow the token came from — the URL hash from
// Supabase doesn't tell us which.
//
// The hash is cleared as part of the navigation so a refresh doesn't
// re-trigger.
// =============================================================================

interface AuthErrorParams {
  error: string
  error_code: string
  error_description: string
}

function parseAuthErrorHash(hash: string): AuthErrorParams | null {
  if (!hash.startsWith('#')) return null
  const params = new URLSearchParams(hash.slice(1))
  const error = params.get('error')
  if (!error) return null
  return {
    error,
    error_code: params.get('error_code') ?? '',
    error_description: (params.get('error_description') ?? '').replace(/\+/g, ' '),
  }
}

export function AuthErrorBoundary() {
  const navigate = useNavigate()

  useEffect(() => {
    const params = parseAuthErrorHash(window.location.hash)
    if (!params) return

    const banner =
      params.error_code === 'otp_expired'
        ? 'That verification link is expired or already used. Sign in below, or use Resend / Forgot Password to get a new one.'
        : params.error_description || 'Authentication failed.'

    navigate('/sign-in', {
      replace: true,
      state: {
        authError: banner,
        fromAuthHash: true,
      },
    })
  }, [navigate])

  return null
}
